import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Settings, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { API_URL } from '../../config/api';
import { authFetch } from '../../utils/auth-fetch';
import { toast } from '../ui/use-toast';

interface CatalogAdminToolsProps {
  siteId: string;
  onRolesAssigned?: () => void;
}

export const CatalogAdminTools: React.FC<CatalogAdminToolsProps> = ({ siteId, onRolesAssigned }) => {
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<{
    success: boolean;
    assigned: number;
    skipped: number;
    totalPages: number;
  } | null>(null);

  const handleAssignRoles = async () => {
    try {
      setIsAssigning(true);
      setAssignmentResult(null);
      
      const response = await authFetch(`/catalog-admin/sites/${siteId}/assign-page-roles`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to assign page roles');
      }

      const result = await response.json();
      setAssignmentResult(result);
      
      toast({
        title: 'Page Roles Assigned',
        description: `Successfully assigned roles to ${result.assigned} pages`,
      });
      
      // Trigger refresh of catalog if callback provided
      if (onRolesAssigned) {
        onRolesAssigned();
      }
    } catch (error) {
      console.error('Error assigning page roles:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign page roles',
        variant: 'destructive',
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Catalog Admin Tools
        </CardTitle>
        <CardDescription>
          Manage and optimize your product catalog
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h3 className="text-sm font-medium mb-2">Auto-Assign Page Roles</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Automatically categorize pages as products, services, or other types based on their content and URL patterns.
            This helps ensure only actual product pages appear in the catalog.
          </p>
          
          <Button 
            onClick={handleAssignRoles} 
            disabled={isAssigning}
            className="w-full sm:w-auto"
          >
            {isAssigning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning Roles...
              </>
            ) : (
              <>
                <Settings className="h-4 w-4 mr-2" />
                Assign Page Roles
              </>
            )}
          </Button>
        </div>

        {assignmentResult && (
          <Alert className={assignmentResult.success ? '' : 'border-destructive'}>
            {assignmentResult.success ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Role Assignment Complete</p>
                <p className="text-sm">
                  • Total pages analyzed: {assignmentResult.totalPages}
                </p>
                <p className="text-sm">
                  • Roles assigned: {assignmentResult.assigned}
                </p>
                <p className="text-sm">
                  • Pages skipped (already had roles): {assignmentResult.skipped}
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="pt-4 border-t">
          <h3 className="text-sm font-medium mb-2">How Page Filtering Works</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Pages with role "ecommerce.product" or "service.detail" are included</li>
            <li>• Pages with product-like URLs (/product/, /item/, /shop/) are analyzed</li>
            <li>• Pages must have either a price or product image to be included</li>
            <li>• Common non-product pages (home, about, contact) are excluded</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};